import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

import {
  IFBApplicant,
  IFBApplication,
  IFBApplicationFileInfo,
  IFBJobApplicationSettings,
  IFBPagedResponseHttp,
  IFBReferralApplication
} from '@interfaces';
import {
  IFBApplicationFeedbackResponse,
  IFBApplicationHistoryResponse,
  IFBChangeStatusPayload,
  IFBNoRewardReason
} from '@app/models/application';
import { IFBCustomFormResponse } from '@app/models/custom-form';
import { CompanyService } from '@app/shared/services/company.service';

@Injectable({
  providedIn: 'root'
})
export class JobApplicationResource {
  constructor(
    private companyService: CompanyService,
    private http: HttpClient
  ) {}

  private get url() {
    return `https://${environment.API_BASE}/v1/companies/${this.companyService.data.id}`;
  }

  create(payload: IFBReferralApplication, recaptchaToken: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/x.firstbird.jobapplication+json;version=2'
    });
    if (recaptchaToken) {
      headers = headers.append('X-Recaptcha-Token', recaptchaToken);
    }
    return this.http.post<IFBReferralApplication>(`${this.url}/applications`, payload, { headers });
  }

  get(params?: { limit: number; offset: number; status: string }) {
    const headers = new HttpHeaders({
      'Accept-version': 'v2'
    });

    return this.http.get<IFBPagedResponseHttp<IFBApplication>>(`${this.url}/applications`, { headers, params });
  }

  getById(id: string, params?) {
    const headers = new HttpHeaders({
      'Accept-version': 'v2'
    });

    return this.http.get<IFBApplication>(`${this.url}/applications/${id}`, { params, headers });
  }

  getFeedbacks(applicationId: string) {
    const url = `${this.url}/applications/${applicationId}/referrals`;
    return this.http.get<IFBApplicationFeedbackResponse>(url);
  }

  getFileInfo(id: string, applicationId: string) {
    const url = `${this.url}/applications/${applicationId}/files/${id}`;
    return this.http.get<IFBApplicationFileInfo>(url);
  }

  getFileUrl(id: string, applicationId: string) {
    const url = `${this.url}/applications/${applicationId}/files/${id}/url`;
    return this.http.get<{ url: string }>(url);
  }

  getHistory(applicationId: string) {
    const url = `${this.url}/applications/${applicationId}/history`;
    const headers = new HttpHeaders({
      'Accept-version': 'v2'
    });
    return this.http.get<IFBApplicationHistoryResponse>(url, { headers });
  }

  getRatingCount(rating: string) {
    return this.http.get<{ items: IFBApplication[] }>(`${this.url}/applications?limit=1&rating=${rating}`);
  }

  getResponse(id: string) {
    return this.http.get<IFBCustomFormResponse>(`${this.url}/applications/${id}/response`);
  }

  getSettings() {
    const url = `${this.url}/settings/jobapplication`;
    return this.http.get<IFBJobApplicationSettings>(url);
  }

  updateSettings(body: IFBJobApplicationSettings) {
    const url = `${this.url}/settings/jobapplication`;
    return this.http.put<void>(url, body);
  }

  changeRating(referralId: string, payload: { value: string; message: string }) {
    const url = `${this.url}/applications/${referralId}/rating`;
    return this.http.put<void>(url, payload);
  }

  changeStatus(referralId: string, payload: IFBChangeStatusPayload) {
    const url = `${this.url}/applications/${referralId}/status`;
    return this.http.put<void>(url, payload);
  }

  getNoRewardReasons() {
    const url = `https://${environment.API_BASE}/v1/applications/no_reward_reasons`;
    return this.http.get<IFBNoRewardReason[]>(url);
  }

  getApplicationChannel(applicationId: string) {
    const url = `${this.url}/applications/${applicationId}/?fields=channel`;
    return this.http.get<{ channel: string }>(url);
  }

  update(id: string, payload: IFBApplicant) {
    const url = `${this.url}/applications/${id}`;
    return this.http.put<void>(url, payload);
  }

  remove(id: string) {
    return this.http.delete(`${this.url}/applications/${id}`);
  }
}
